import Head from 'next/head'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useSelector } from 'react-redux'

import styles from './search-content-header.module.scss'
import { ButtonHierarchy } from '@/components/styleguide/Button'
import { InternalLink } from '@/components/styleguide/Link'
import { SearchBreadcrumbs } from '@/components/views/SearchBreadcrumbs'
import { constants, FILTER_LEASE_TYPES_PERSONAL } from '@/lib/constants'
import { ROUTES } from '@/lib/routes'
import { addVatToPrice } from '@/lib/utilities/search'
import { getVehicleHeroImage } from '@/lib/utilities/vehicle'
import { selectSearchResultDeals } from '@/store/selectors/search'
import { PageContent } from '@/types/search'
import { Size } from '@/types/system'
import { isProd } from '@/lib/utilities/system'

type SearchContentHeaderProps = {
  pageContent: PageContent
  isVans: boolean
  showBreadcrumbs: boolean
  showLinks?: boolean
  isBottom?: boolean
}
export const SearchContentHeader = ({
  pageContent,
  isVans,
  showBreadcrumbs = true,
  showLinks = true,
  isBottom = false,
}: SearchContentHeaderProps) => {
  const [isIntroContentFullyVisible, setIsIntroContentFullyVisible] = useState(false)
  const searchContent = useSelector(selectSearchResultDeals)
  const [content, setContent] = useState(
    pageContent.extra
      ? pageContent.intro.concat('\n').concat(pageContent.extra)
      : pageContent.intro,
  )

  useEffect(() => {
    setContent(
      pageContent.extra
        ? pageContent.intro.concat('\n').concat(pageContent.extra)
        : pageContent.intro,
    )
  }, [pageContent])

  return (
    <>
      {!!pageContent && !!searchContent[0]?.range && searchContent.length > 0 && (
        <Head>
          <script type="application/ld+json">
            {`
          {
            "@context":"http://www.schema.org",
            "@type":"Product",
            "brand":"${searchContent[0].manufacturer}",
            "logo":"${pageContent.image}",
            "name":"${searchContent[0].manufacturer} ${searchContent[0].range}",
            "category":"${searchContent[0].manufacturer}",
            "image":"${getVehicleHeroImage(searchContent[0].profileImage)}",
            "description":"${searchContent[0].manufacturer} ${searchContent[0].range
              } offers, personal pricing inc. VAT.",
            "offers":{
              "@type":"AggregateOffer",
              "lowPrice":"${searchContent[0].profile.leaseType === FILTER_LEASE_TYPES_PERSONAL
                ? addVatToPrice(searchContent[0].profile.price)
                : searchContent[0].profile.price.toFixed(2)
              }",
              "priceCurrency":"GBP"
            }
          }
        `}
          </script>
          <script type="application/ld+json">
            {`
          {
            "@context":"http://schema.org",
            "@type":"Car",
            "name":"${searchContent[0].manufacturer} ${searchContent[0].range}",
            "aggregateRating":{
                "@type":"AggregateRating",
                "ratingValue":"${searchContent[0].rating}",
                "bestRating":"100",
                "ratingCount":"${searchContent.length}"
            }
          }
        `}
          </script>
        </Head>
      )}
      <div className={`${styles.main} ${isBottom ? styles.bottom : ''}`}>
        <div className={styles.wrapper}>
          <div className={styles.content}>
            {showBreadcrumbs && <SearchBreadcrumbs isVans={isVans} pageContent={pageContent} />}
            <h1 className={!isProd() ? 'search-content__h1' : ''}>{pageContent.title}</h1>
            <div className={styles.body}>
              <ReactMarkdown
                className={`${styles.markdown} ${isIntroContentFullyVisible ? styles.show : ''}`}>{content}</ReactMarkdown>
              {!isIntroContentFullyVisible && (
                <button
                  type="button"
                  className={styles.button}
                  onClick={() => setIsIntroContentFullyVisible(true)}>
                  Read more
                </button>
              )}
            </div>
          </div>
          {showLinks && (
            <div className={styles.links}>
              <div className={styles.item}>
                <div className={styles.image}>
                  <Image
                    src={`${constants.imageHost.assets}/icons/amazon-reward.png`}
                    alt=""
                    height={36}
                    width={36}
                  />
                </div>
                <div className={styles.link}>
                  <p>£20 Amazon voucher with every lease</p>
                  <InternalLink
                    href={ROUTES.rewardsAndReferralsPage.url}
                    hierarchy={ButtonHierarchy.Bezeled}
                    size={Size.Medium}>
                    More about LocoRewards
                  </InternalLink>
                </div>
              </div>
              <div className={styles.item}>
                <div className={styles.image}>
                  <Image
                    src={`${constants.imageHost.assets}/icons/wink-with-background.png`}
                    alt=""
                    height={36}
                    width={36}
                  />
                </div>
                <div className={styles.link}>
                  <p>Road tax &amp; breakdown cover included</p>
                  <InternalLink
                    href={ROUTES.whyLeasePage.url}
                    size={Size.Medium}
                    hierarchy={ButtonHierarchy.Bezeled}>
                    Learn more benefits of leasing
                  </InternalLink>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
